import { GetStaticProps } from 'next';
import React from 'react';

import { VacancyOverviewSingleFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetVacancyOverview } from 'api/graphql-helper';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { Vacancy, initialState as vacanciesInitialState } from 'redux/stores/vacancies';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';
import { fetchFormatVacancies } from 'utils/fetch-format-vacancies';

interface IVacancyOverviewPageProps extends IBasePageProps {
	vacancyOverview?: VacancyOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const VacancyOverviewPage = (props: IVacancyOverviewPageProps): React.JSX.Element => {
	const modules = props.vacancyOverview?.modules || [];
	const seoModule = props?.vacancyOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props.pageInformation?.seo} />
			<IntroHeader module={props?.vacancyOverview?.header} slug={ROUTES.VACANCIES.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.VACANCIES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetVacancyOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, vacancyOverview } = result.props.data;

		if (vacancyOverview == null) {
			return {
				notFound: true
			};
		}

		const vacancies: Vacancy[] = await fetchFormatVacancies(locale, 'public');

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				vacancyOverview: vacancyOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'vacancy-overview',
						contentTypeId: vacancyOverview?.data?.documentId
					},
					vacancies: {
						...vacanciesInitialState,
						vacancies: vacancies,
						totalVacancies: vacancies.length
					}
				}
			},
			revalidate: 600
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in vacancy overview', error);

		return {
			notFound: true
		};
	}
};

export default VacancyOverviewPage;
